import { createContext, useContext, useMemo, useState, ReactNode } from "react";

/**
 * The ensemble member currently committed on the AnimationLayer scenario
 * slider, published so consumers outside the layer (e.g. the impacted-asset
 * colouring in AssetsLayer) can resolve per-member data. `pointsPerMember`
 * rides along because mapping a base ignition point to the member's instance
 * of it (partition idx = memberIdx * pointsPerMember + pointIdx) needs it.
 *
 * Null whenever there is no member-scoped view to reflect: single (non
 * ensemble) runs, or an ensemble that hasn't been engaged with yet.
 */
export interface EnsembleMemberSelection {
  memberIdx: number;
  pointsPerMember: number;
  /**
   * The AnimationLayer view the selection reflects: "member" = one scenario
   * (the slider member), "confidence" = the across-member burn-probability
   * view. Consumers that resolve per-member data (AssetsLayer) use memberIdx
   * either way; Inspection branches on it (member vs all-members lookup).
   */
  view: "member" | "confidence";
}

interface EnsembleMemberContextValue {
  memberSelection: EnsembleMemberSelection | null;
  setMemberSelection: (selection: EnsembleMemberSelection | null) => void;
}

const EnsembleMemberContext = createContext<EnsembleMemberContextValue>({
  memberSelection: null,
  setMemberSelection: () => {},
});

export const EnsembleMemberProvider = ({ children }: { children: ReactNode }) => {
  const [memberSelection, setMemberSelection] =
    useState<EnsembleMemberSelection | null>(null);

  const value = useMemo(
    () => ({ memberSelection, setMemberSelection }),
    [memberSelection],
  );

  return (
    <EnsembleMemberContext.Provider value={value}>
      {children}
    </EnsembleMemberContext.Provider>
  );
};

export const useEnsembleMember = () => useContext(EnsembleMemberContext);
